import clsx from 'clsx';
import { MessageCircle, SpellCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { ChatHeaderControls } from './ChatHeaderControls';
import { ProofreadHeaderControls } from './ProofreadHeaderControls';

export type ChatVersion = 'agent_v2' | 'agent';

interface WorkspaceChatHeaderProps {
  matterId: string;
  workspaceId?: string;
  documentId?: string;
  exampleDemandLetterId?: string;
  mode: 'chat' | 'proofreading';
  onChangeMode: (mode: 'chat' | 'proofreading') => void;
  chatVersion: ChatVersion;
  onChangeChatVersion: (v: ChatVersion) => void;
  showVersionToggle?: boolean;
  className?: string;
}

const ChatVersionToggle = ({
  chatVersion,
  onToggle,
}: {
  chatVersion: ChatVersion;
  onToggle: (v: ChatVersion) => void;
}) => (
  <div className="flex items-center gap-0.5 rounded-md bg-gray-200/60 p-0.5">
    <button
      type="button"
      onClick={() => onToggle('agent_v2')}
      className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
        chatVersion === 'agent_v2'
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      Agent V2
    </button>
    <button
      type="button"
      onClick={() => onToggle('agent')}
      className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
        chatVersion === 'agent'
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      Agent
    </button>
  </div>
);

export const WorkspaceChatHeader = ({
  matterId,
  workspaceId,
  documentId,
  exampleDemandLetterId,
  mode,
  onChangeMode,
  chatVersion,
  onChangeChatVersion,
  showVersionToggle = false,
  className,
}: WorkspaceChatHeaderProps) => {
  // no chat specific state needed here, handled by child components

  return (
    <div
      className={clsx(
        'w-full bg-white border-b border-gray-100 h-full',
        className,
      )}
    >
      <div className="flex flex-row items-center justify-between py-1 px-2 space-x-2 h-full">
        {/* Left side: mode-specific controls */}
        <div className="w-full space-x-2 items-center flex">
          {mode === 'chat' ? (
            chatVersion === 'agent' ? (
              <ChatHeaderControls
                matterId={matterId}
                workspaceId={workspaceId}
              />
            ) : null
          ) : (
            <ProofreadHeaderControls
              matterId={matterId}
              workspaceId={workspaceId}
              documentId={documentId}
            />
          )}
          {mode === 'chat' && showVersionToggle && (
            <ChatVersionToggle
              chatVersion={chatVersion}
              onToggle={onChangeChatVersion}
            />
          )}
        </div>

        {/* Mode switch button */}
        <Button
          variant={mode === 'proofreading' ? 'outline' : 'default'}
          size="sm"
          onClick={() =>
            onChangeMode(mode === 'chat' ? 'proofreading' : 'chat')
          }
          className={clsx(
            'whitespace-nowrap flex items-center gap-2',
            mode === 'chat'
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50',
          )}
        >
          {mode === 'chat' ? (
            <>
              <SpellCheck className="h-4 w-4" />
              Switch to Proofreading
            </>
          ) : (
            <>
              <MessageCircle className="h-4 w-4" />
              Switch to Chat
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
